import axios from '../helpers/axios'
import { logError } from '../services';
import { IDropdownResponse, IPartCategory, IPartListItem, IPartPrice, IPartPriceRes, IPartRes, IResponse, IReturnPartPO, IMaterialListItem, IPartGeneralInfo, IMaterialGeneralInfo } from '../models';

export const getPartCategories = async (filters?: { partCategoryId?: number }): Promise<IPartCategory[]> => {
  try {
    const { data } = await axios.get(`/api/parts/categories`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getPartCategories failed');
    return Promise.reject(error);
  }
};

export const getParts = async (filters?: {
  page?: number;
  perPage?: number | string;
  sortBy?: string;
  sortDirection?: string;
  name?: string;
  partCategoryId?: number;
  id?: number;
  vendorId?: number | string;
}): Promise<IPartRes> => {
  try {
    const { data } = await axios.get(`/api/parts`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getParts failed');
    return Promise.reject(error);
  }
};

export const getPartPrice = async (filters?: {
  id?: number;
  ids?: number[];
  sortBy?: string;
  sortDirection?: string;
  name?: string;
  partCategoryId?: number;
  vendorId?: number;
}): Promise<IPartPriceRes> => {
  try {
    const { data } = await axios.get(`/api/parts/price`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getPartPrice failed');
    return Promise.reject(error);
  }
};

export const getPartPriceRecommended = async (
  partId: string | number,
  filters?: {
    neighborhoodId?: number | string;
    lotNumber?: number | string | null;
  }
): Promise<IPartPrice | {}> => {
  try {
    const res = await axios.get(`/api/parts/${partId}/prices/recommended`, {
      params: filters
    });
    // some calls will return a 204 "No Content" and no `data` so mock some data so calls still go through to FE
    if (res.status === 204) {
      return {};
    } else {
      return res.data;
    }
  } catch (error) {
    logError(error, 'getPartPriceRecommended failed');
    return Promise.reject(error);
  }
};

export const getReturnParts = async (filters?: {
  fieldOrderId?: number;
  vendorId?: number | string;
  neighborhoodId?: number | string;
  lotNumber?: number | string | null;
  sortBy?: string;
  sortDirection?: string;
  page?: string | number;
  perPage?: string | number;
}): Promise<IReturnPartPO[]> => {
  try {
    const { data } = await axios.get(`/api/parts/return-po`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getReturnParts failed');
    return Promise.reject(error);
  }
};

export const getPartList = async (filters?: {
  name?: string;
  partClassId?: number | string;
  sortBy?: string;
  sortDirection?: string;
  page?: string | number;
  perPage?: string | number;
}): Promise<IResponse<IPartListItem[]>> => {
  try {
    const { data } = await axios.get(`/api/parts/list`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getPartList failed');
    return Promise.reject(error);
  }
};

export const getPartClassChildren = async (partClassId?: string): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/parts/part-class/children`, {
      params: {partClassId}
    });
    return data;
  } catch (error) {
    logError(error, 'getPartClassChildren failed');
    return Promise.reject(error);
  }
};

export const getMaterialList = async (filters?: {
  name?: string;
  materialClassId?: number | string;
  sortBy?: string;
  sortDirection?: string;
  page?: string | number;
  perPage?: string | number;
}): Promise<IResponse<IMaterialListItem[]>> => {
  try {
    const { data } = await axios.get(`/api/parts/materials/list`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getMaterialList failed');
    return Promise.reject(error);
  }
};

export const getMaterialClassChildren = async (materialClassId?: string): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/parts/materials/material-class/children`, {
      params: {materialClassId}
    });
    return data;
  } catch (error) {
    logError(error, 'getMaterialClassChildren failed');
    return Promise.reject(error);
  }
};

export const createPart = async (data): Promise<any> => {
  try {
    return await axios.post(`/api/parts`, data);
  } catch (error) {
    logError({ error }, 'createPart failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getPartGeneralInfo = async (
  partId: number | string
): Promise<IPartGeneralInfo> => {
  try {
    const { data } = await axios.get(`/api/parts/${partId}`);
    return data;
  } catch (error) {
    logError(error, 'getPartGeneralInfo failed');
    return Promise.reject(error?.response?.data);
  }
};

export const putPartGeneralInfo = async (
  partId: number | string,
  payload: IPartGeneralInfo
): Promise<any> => {
  try {
    const { data } = await axios.put(
      `/api/parts/${partId}`,
      payload
    );
    return data;
  } catch (error) {
    logError({ error, payload: payload }, 'putPartGeneralInfo failed');
    return Promise.reject(error?.response?.data);
  }
};

// API call to save image file to parts //

export const createPartsFile = async (
  partId: string | number,
  file: FormData | string
): Promise<string[]> => {
  try {
    const { data } = await axios.post(
      `/api/parts/${partId}/file`,
      file
    );
    return data;
  } catch (error) {
    logError(error, 'createPartsFile failed');
    return Promise.reject(error.response?.data ?? { message: 'Error creating part.' });
  }
};

export const getPartFile = async (
  partId: number | string
): Promise<any> => {
  try {
    const response = await axios.get(
      `/api/parts/${partId}/file`,
      { responseType: 'blob' }
    );
    return response;
  } catch (error) {
    logError(error, 'getPartFile failed');
    return Promise.reject(error.response?.data ?? { message: 'Error fetching image.' });
  }
};

export const getPartClassParents = async (partClassId?: string): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/parts/part-class/parents`, {
      params: {partClassId}
    });
    return data;
  } catch (error) {
    logError(error, 'getPartClassParents failed');
    return Promise.reject(error);
  }
};

export const getMaterialGeneralInfo = async (
  materialId: number | string
): Promise<IMaterialGeneralInfo> => {
  try {
    const { data } = await axios.get(`/api/parts/materials/${materialId}`);
    return data;
  } catch (error) {
    logError(error, 'getMaterialGeneralInfo failed');
  }
};

export const createMaterial = async (data): Promise<any> => {
  try {
    return await axios.post(`/api/parts/materials`, data);
  } catch (error) {
    logError({ error }, 'createMaterial failed');
    return Promise.reject(error?.response?.data);
  }
};

export const putMaterialGeneralInfo = async (
  materialId: number | string,
  payload: IMaterialGeneralInfo
): Promise<any> => {
  try {
    const { data } = await axios.put(
      `/api/parts/materials/${materialId}`,
      payload
    );
    return data;
  } catch (error) {
    logError({ error, payload: payload }, 'putMaterialGeneralInfo failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getBidFrequencies = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/parts/materials/lookups/bid-frequencies`);
    return data;
  } catch (error) {
    logError(error, 'getBidFrequencies failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getMaterialTasks = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/parts/materials/lookups/tasks`);
    return data;
  } catch (error) {
    logError(error, 'getTasks failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getMaterialTerritories = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/parts/materials/lookups/territories`);
    return data;
  } catch (error) {
    logError(error, 'getMaterialTerritories failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getSuppliers = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/parts/materials/lookups/material-suppliers`);
    return data;
  } catch (error) {
    logError(error, 'getSuppliers failed');
    return Promise.reject(error?.response?.data);
  }
};

export const deleteMaterial = async (materialId: string): Promise<void> => {
  try {
    const { data } = await axios.delete(`/api/parts/materials/${materialId}`);
    return data;
  } catch (error) {
    logError(error, 'deleteMaterial failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getMaterialClassParents = async (materialClassId?: string): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/parts/materials/material-class/parents`, {
      params: {materialClassId}
    });
    return data;
  } catch (error) {
    logError(error, 'getMaterialClassParents failed');
    return Promise.reject(error);
  }
};